import { useCallback, useEffect, useRef, useState } from 'react';
import cartService, { CartItem, CartOptions } from '../services/cartService';
import { useOptionalToast } from '../contexts/ToastContext';
import type { MenuItem } from '../services/restaurantService';
import { getInitialSelections } from '../components/common/MenuOrderOptions/MenuOrderOptions';
import type { MenuLinePromptState } from '../components/common/MenuItemOptionsSheet/MenuLinePrompt';
import {
  cartLineKey,
  cartLineSummary,
  cartLinesForMenu,
  cartOptionsFor,
  cartOptionsForLine,
  selectionLineKey,
  selectionsFromCartLine,
} from '../utils/cartLines';
import {
  buildOptionsSummary,
  itemHasOptions,
  MenuOrderSelections,
  normalizeNote,
} from '../utils/menuOptions';

export interface CartActionResult {
  success: boolean;
  message?: string;
}

export interface MenuCardAddToCartFlags {
  /**
   * The caller shows the error itself (inside the open options sheet), so the page must not
   * toast it as well.
   */
  quietErrors?: boolean;
}

/**
 * Sets one cart line's quantity (0 removes it). `itemQuantity` is the item's total across all of
 * its lines after the change, for the card badge.
 */
export type MenuCardAddToCart = (
  menuId: number,
  quantity: number,
  options?: CartOptions,
  itemQuantity?: number,
  flags?: MenuCardAddToCartFlags
) => Promise<CartActionResult | void> | CartActionResult | void;

interface LocalLine {
  key: string;
  quantity: number;
  selections: MenuOrderSelections;
  /** What /cart/add needs to identify the line: the stored identity for lines read from the cart. */
  options: CartOptions;
  summary: string;
  order: number;
}

const CART_LOAD_ERROR = 'Could not load your cart. Please try again.';

function total(lines: LocalLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity, 0);
}

function latest(lines: LocalLine[]): LocalLine | undefined {
  return lines.reduce<LocalLine | undefined>((a, b) => (!a || b.order > a.order ? b : a), undefined);
}

function toLocal(lines: CartItem[]): LocalLine[] {
  const byId = lines.every((l) => typeof l.cartId === 'number');
  return lines.map((line, index) => ({
    key: cartLineKey(line),
    quantity: line.quantity,
    selections: selectionsFromCartLine(line),
    options: cartOptionsForLine(line),
    summary: cartLineSummary(line),
    order: byId ? line.cartId : index,
  }));
}

/**
 * Quantity controls for a browse card. Items without options keep one-tap add; items with
 * options open the sheet, or offer "Repeat last" when already in the cart. Every change is
 * applied to one specific cart line (design D5) and changes are serialised so rapid taps
 * do not overwrite each other.
 */
export function useMenuItemCartControls(item: MenuItem, appName: string, onAddToCart: MenuCardAddToCart) {
  const [quantity, setQuantityState] = useState(item.qty || 0);
  const quantityRef = useRef(item.qty || 0);
  const [selections, setSelections] = useState<MenuOrderSelections>(() => getInitialSelections(item));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [sheetBusy, setSheetBusy] = useState(false);
  const [prompt, setPrompt] = useState<MenuLinePromptState | null>(null);
  const linesRef = useRef<LocalLine[] | null>(null);
  const repeatKeyRef = useRef<string | null>(null);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const itemRef = useRef(item);
  itemRef.current = item;
  const toast = useOptionalToast();
  const hasOptions = itemHasOptions(item);

  const setQuantity = (q: number) => {
    quantityRef.current = q;
    setQuantityState(q);
  };

  useEffect(() => {
    const q = item.qty || 0;
    quantityRef.current = q;
    setQuantityState(q);
  }, [item.qty]);

  useEffect(() => {
    setSelections(getInitialSelections(item));
    linesRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  useEffect(() => {
    const closeSheet = () => {
      setSheetOpen(false);
      setPrompt(null);
    };
    window.addEventListener('maznut:close-menu-options-sheet', closeSheet);
    return () => window.removeEventListener('maznut:close-menu-options-sheet', closeSheet);
  }, []);

  const run = (task: () => Promise<void>) => {
    chainRef.current = chainRef.current.then(task).catch(() => undefined);
  };

  /** This item's cart lines; fetched only when the local view is unknown or out of date. */
  const loadLines = async (): Promise<LocalLine[] | null> => {
    const current = quantityRef.current;
    if (current <= 0) {
      linesRef.current = [];
      return [];
    }
    const known = linesRef.current;
    if (known && total(known) === current) {
      return known;
    }
    try {
      const res = await cartService.getCart(appName);
      if (!res.success) return null;
      const lines = toLocal(cartLinesForMenu(res.cartItems || [], item.id));
      linesRef.current = lines;
      if (total(lines) !== current) {
        setQuantity(total(lines));
      }
      return lines;
    } catch {
      return null;
    }
  };

  const summaryFor = (sel: MenuOrderSelections) =>
    [buildOptionsSummary(item, sel), normalizeNote(sel.itemNote) ? `“${normalizeNote(sel.itemNote)}”` : '']
      .filter(Boolean)
      .join(' · ');

  /** A line for a new selection (identity built from the picker). */
  const targetFor = (sel: MenuOrderSelections): Omit<LocalLine, 'quantity' | 'order'> => ({
    key: selectionLineKey(item.id, sel, item),
    selections: sel,
    options: cartOptionsFor(sel, item),
    summary: summaryFor(sel),
  });

  /**
   * Sets the quantity of one line, updating the local view first. An existing line is always
   * addressed with its stored identity, never one re-derived from the selection.
   */
  const setLine = async (
    target: Omit<LocalLine, 'quantity' | 'order'>,
    nextQty: number,
    flags?: MenuCardAddToCartFlags
  ): Promise<CartActionResult> => {
    const lines = (linesRef.current || []).slice();
    const idx = lines.findIndex((l) => l.key === target.key);
    const options = idx >= 0 ? lines[idx].options : target.options;
    const qty = Math.max(0, nextQty);
    if (qty === 0) {
      if (idx >= 0) lines.splice(idx, 1);
    } else if (idx >= 0) {
      lines[idx] = { ...lines[idx], quantity: qty };
    } else {
      const top = latest(lines);
      lines.push({ ...target, quantity: qty, order: (top?.order ?? 0) + 1 });
    }
    const before = quantityRef.current;
    const after = total(lines);
    linesRef.current = lines;
    setQuantity(after);
    const result = await onAddToCart(item.id, qty, options, after, flags);
    if (result && result.success === false) {
      linesRef.current = null;
      setQuantity(before);
      return result;
    }
    return { success: true };
  };

  // Reads the latest item through a ref so a refreshed menu payload (prices, options) is used.
  const openSheet = useCallback(() => {
    setSelections(getInitialSelections(itemRef.current));
    setSheetError(null);
    setSheetOpen(true);
  }, []);

  const handleIncrement = () =>
    run(async () => {
      if (!hasOptions) {
        const target = targetFor(getInitialSelections(item));
        const lines = await loadLines();
        // Unknown cart (request failed): old behaviour, the item has one plain line.
        const current = lines
          ? lines.find((l) => l.key === target.key)?.quantity ?? 0
          : quantityRef.current;
        await setLine(target, current + 1);
        return;
      }
      if (quantityRef.current <= 0) {
        openSheet();
        return;
      }
      const last = latest((await loadLines()) || []);
      if (!last) {
        openSheet();
        return;
      }
      repeatKeyRef.current = last.key;
      setPrompt({ kind: 'repeat', summary: last.summary });
    });

  const handleDecrement = () =>
    run(async () => {
      if (quantityRef.current <= 0) return;
      const lines = await loadLines();
      if (!lines) {
        if (!hasOptions) {
          await setLine(targetFor(getInitialSelections(item)), quantityRef.current - 1);
        } else {
          // Which line to reduce is unknown without the cart: say so instead of doing nothing.
          toast?.showToast(CART_LOAD_ERROR, 'error');
        }
        return;
      }
      if (lines.length > 1) {
        setPrompt({ kind: 'multiple' });
        return;
      }
      const line = lines[0];
      if (!line) {
        setQuantity(0);
        return;
      }
      await setLine(line, line.quantity - 1);
    });

  const repeatLast = () =>
    run(async () => {
      setPrompt(null);
      const line = (linesRef.current || []).find((l) => l.key === repeatKeyRef.current);
      if (line) {
        await setLine(line, line.quantity + 1);
      }
    });

  const chooseNew = () => {
    setPrompt(null);
    openSheet();
  };

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    setSheetError(null);
  }, []);

  const closePrompt = useCallback(() => setPrompt(null), []);

  const confirmSheet = (qty: number) =>
    run(async () => {
      const sel = selections;
      setSheetBusy(true);
      try {
        const lines = await loadLines();
        if (!lines) {
          // Without the cart the absolute quantity of a matching line is unknown.
          setSheetError(CART_LOAD_ERROR);
          return;
        }
        const target = targetFor(sel);
        const existing = lines.find((l) => l.key === target.key)?.quantity ?? 0;
        // The sheet shows the error itself; the page must not toast it too.
        const result = await setLine(target, existing + Math.max(1, qty), { quietErrors: true });
        if (result.success) {
          setSheetError(null);
          setSheetOpen(false);
        } else {
          setSheetError(result.message || 'Could not add this item');
        }
      } finally {
        setSheetBusy(false);
      }
    });

  return {
    quantity,
    hasOptions,
    selections,
    setSelections,
    sheetOpen,
    sheetError,
    sheetBusy,
    openSheet,
    closeSheet,
    confirmSheet,
    prompt,
    closePrompt,
    repeatLast,
    chooseNew,
    handleIncrement,
    handleDecrement,
  };
}
